/**
 * LIVE SUBTITLES mode (spec §15).
 *
 * Optimised for being read from three metres away, or projected. Everything
 * except the text gets out of the way: the chrome fades until the pointer
 * moves, fullscreen is one click or `F`, and the text itself scales from a
 * single slider that drives both panels.
 *
 * Font sizing is a CSS custom property rather than inline sizes, so the two
 * lines stay in proportion and the whole stage can be scaled with the system
 * zoom without a reflow bug.
 */

import { useEffect, useRef, useState } from "react";
import type { LanguageCode } from "@shared/types/language";
import { languageShortLabel } from "@shared/languages";
import type { TranslationSession } from "../lib/use-session";
import { Badge, Button, IconButton, Segmented } from "./ui";
import { IconFullscreen, IconMic, IconMoon, IconSpeaker, IconStop, IconSun } from "./icons";

interface Props {
  session: TranslationSession;
  sourceLanguage: LanguageCode | "auto";
  targetLanguage: LanguageCode;
  onSourceChange: (value: LanguageCode | "auto") => void;
  onTargetChange: (value: LanguageCode) => void;
  showOriginal: boolean;
  showTranslation: boolean;
  subtitleSize: number;
  subtitlePosition: "top" | "middle" | "bottom";
  theme: "dark" | "light";
  onSizeChange: (size: number) => void;
  onPositionChange: (position: "top" | "middle" | "bottom") => void;
  onThemeChange: (theme: "dark" | "light") => void;
  canSpeak: (language: LanguageCode) => boolean;
  onSpeak: (text: string, language: LanguageCode) => void;
}

export function SubtitleMode(props: Props) {
  const { session } = props;
  const stageRef = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const exitFullscreen = async (): Promise<void> => {
    await document.exitFullscreen().catch(() => undefined);
  };

  const toggleFullscreen = async (): Promise<void> => {
    if (document.fullscreenElement) await exitFullscreen();
    else await stageRef.current?.requestFullscreen().catch(() => undefined);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "f" || event.key === "F") void toggleFullscreen();
      if (event.key === " " && !isTypingTarget(event.target)) {
        event.preventDefault();
        void (session.isLive ? session.stop() : session.start());
      }
      if (event.key === "Escape" && fullscreen) void exitFullscreen();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    const onChange = (): void => setFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const partial = session.partial;
  const lastEntry = session.transcript.entries[session.transcript.entries.length - 1];
  const sourceText = partial?.source || lastEntry?.sourceText || "";
  const targetText = partial?.target || lastEntry?.translatedText || "";

  return (
    <div
      className={`substage substage--${props.subtitlePosition}`}
      ref={stageRef}
      style={{ ["--sub-size" as string]: props.subtitleSize.toFixed(2) }}
    >
      <div className="substage__chrome">
        <Segmented
          label="Subtitle position"
          options={[
            { value: "top", label: "Top" },
            { value: "middle", label: "Middle" },
            { value: "bottom", label: "Bottom" },
          ]}
          value={props.subtitlePosition}
          onChange={props.onPositionChange}
        />
        <IconButton label={props.theme === "dark" ? "Switch to light" : "Switch to dark"} onClick={() => props.onThemeChange(props.theme === "dark" ? "light" : "dark")}>
          {props.theme === "dark" ? <IconSun size={16} /> : <IconMoon size={16} />}
        </IconButton>
        <IconButton label="Toggle fullscreen (F)" onClick={() => void toggleFullscreen()}>
          <IconFullscreen size={16} />
        </IconButton>
      </div>

      <div className="substage__stack">
        {props.showOriginal && sourceText ? (
          <p className="subtitle subtitle--source" dir="auto" lang={session.detection?.code ?? undefined}>
            {sourceText}
            {session.isLive && partial ? <span className="stream__cursor" /> : null}
          </p>
        ) : null}

        {props.showTranslation && targetText ? (
          <p className="subtitle subtitle--target" dir="auto" lang={props.targetLanguage} aria-live="polite">
            {targetText}
          </p>
        ) : null}

        {!sourceText && !targetText ? (
          <div className="dim" style={{ textAlign: "center", maxWidth: "40ch", margin: "0 auto" }}>
            {session.isLive ? "Listening…" : "Press the microphone to begin live subtitles."}
          </div>
        ) : null}
      </div>

      <div className="subdock">
        {session.transcript.entries.length > 0 ? (
          <div className="row" style={{ gap: "var(--space-2xs)" }}>
            <Badge>{session.transcript.entries.length} lines</Badge>
            <IconButton
              label="Speak the last line"
              onClick={() => props.onSpeak(targetText || sourceText, props.targetLanguage)}
              disabled={!props.canSpeak(props.targetLanguage)}
            >
              <IconSpeaker size={16} />
            </IconButton>
          </div>
        ) : null}
        <Button
          variant={session.isLive ? "danger" : "primary"}
          size="lg"
          onClick={() => void (session.isLive ? session.stop() : session.start())}
          disabled={!session.isSupported}
          iconOnly
          title={session.isLive ? "Stop" : "Start (Space)"}
          aria-label={session.isLive ? "Stop" : "Start"}
        >
          {session.isLive ? <IconStop size={20} /> : <IconMic size={20} />}
        </Button>
        <div className="stack" style={{ gap: "var(--space-3xs)", alignItems: "center" }}>
          <span className="eyebrow">
            {languageShortLabel(props.sourceLanguage === "auto" ? (session.detection?.code ?? "en") : props.sourceLanguage)} → {languageShortLabel(props.targetLanguage)}
          </span>
          <input
            className="range"
            type="range"
            min={0.6}
            max={2.4}
            step={0.05}
            value={props.subtitleSize}
            style={{ width: 200, ["--fill" as string]: `${((props.subtitleSize - 0.6) / 1.8) * 100}%` }}
            onChange={(event) => props.onSizeChange(Number(event.target.value))}
            aria-label="Subtitle size"
          />
        </div>
      </div>
    </div>
  );
}

const isTypingTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  return element?.tagName === "INPUT" || element?.tagName === "TEXTAREA" || element?.isContentEditable === true;
};
